import { Component, OnInit } from '@angular/core';
import * as alertifyjs from 'alertifyjs';
import { AuthService } from 'src/app/services/auth/auth.service';
import { CuentasContratoService } from 'src/app/services/perfil-bancario/cuentas-contrato.service';
import { UserService } from 'src/app/services/user/user.service';
import {
  Borrador, DatosCuenta, LineaCambio, clonarComoCT, datosDeCuenta, diferenciasConLaMT, editarCuenta, editarNueva, quitarNueva,
  hayCuentaCreada, ParCuentas, VistaTelefono, agregarEtiqueta, aplicarBorrador, avisosDelBorrador, bloqueosDelBorrador, borradorVacio,
  buscarCuenta, conteoDeEtiquetas, contarCambios, cuentasDeOpcion, frasesDeBitacora, frenosDelBorrador, hayCambios,
  listaDeCambios, moverOpcion, nombreContrato, nombreCuenta, nombreServicio, opcionTieneCambios, opcionesPorPrioridad,
  paresConEtiqueta, paresDeOpcion, parDe, payloadDeCambios, ponerCuenta, ponerOpcion, prepararCorte, quitarEtiqueta,
  simulaOtroContrato, todasLasCuentas, vistaTelefono
} from './cuentas-contrato-borrador';

const MOTIVO_MINIMO = 10;

/**
 * Cuentas por contrato (ACUERDOS A a F). Dos niveles: las opciones de pago en el orden de la app y, al abrir una, sus
 * cuentas en pares MT → CT. Todo cambio va a un borrador; el teléfono, los bloqueos y los conteos se recalculan al
 * instante y nada se escribe hasta «Guardar», que pide motivo. Los bloqueos son las mismas reglas del núcleo
 * (cuentas-contrato-reglas.ts), así que si aquí no bloquea, el servidor tampoco.
 */
@Component({
  selector: 'app-cuentas-contrato',
  templateUrl: './cuentas-contrato.component.html'
})
export class CuentasContratoComponent implements OnInit {
  // Lo guardado, el borrador y lo que sale de los dos
  datos: any = null;
  borrador: Borrador = borradorVacio();
  efectivo: any = null;
  bloqueos: any[] = [];
  /** Regla 14: botón o DI sin cuenta activa del contrato que cobra. Se avisa, no impide guardar (27-oct). */
  avisos: any[] = [];
  frenos: any[] = [];
  cambios: LineaCambio[] = [];
  totalCambios = 0;
  telefono: VistaTelefono = { opciones: [], reordenada: false, perfil: null };
  resumen: { [opcionId: string]: any } = {};
  opciones: any[] = [];
  paresFiltrados: { opcion: any; pares: ParCuentas[] }[] = [];

  cargando = false;
  error: string | null = null;
  seccion: 'cuentas' | 'historial' = 'cuentas';
  opcionAbierta: string | null = null;
  modoTelefono: 'hoy' | 'borrador' = 'borrador';
  resaltado: string | null = null;
  avisoCorte = false;

  // Etiquetas y bancos
  catalogo: any[] = [];
  filtroEtiqueta: string | null = null;
  conteoEtiquetas: { [nombre: string]: number } = {};
  bancos: { [codigo: string]: string } = {};
  /** Catálogo completo de bancos (/api/invoice-credit/banks) para el select del formulario. */
  listaBancos: { bankCode: string; name: string }[] = [];

  // Formulario de clonar o editar una cuenta CT
  formulario: { modo: 'clonar' | 'editar'; base: any; origen: any; tmpId: string | null; datos: DatosCuenta; avisoRif?: boolean } | null = null;
  private contadorNuevas = 0;

  // Guardar
  dialogoGuardar = false;
  motivo = '';
  guardando = false;
  bloqueosServidor: any[] | null = null;
  errorGuardar: string | null = null;
  resultado: string | null = null;

  // Historial
  historial: any = null;
  cargandoHistorial = false;
  errorHistorial: string | null = null;
  paginaHistorial = 1;
  readonly limiteHistorial = 10;
  cambioAVolver: any = null;
  motivoVolver = '';
  volviendo = false;
  explicacionVolver: { titulo: string; lineas: string[] } | null = null;

  readonly motivoMinimo = MOTIVO_MINIMO;
  readonly nombreContrato = nombreContrato;
  readonly nombreServicio = nombreServicio;
  readonly frasesDeBitacora = frasesDeBitacora;

  constructor(private servicio: CuentasContratoService, public _auth: AuthService, private _user: UserService) {}

  ngOnInit(): void {
    this.cargar();
    this.cargarCatalogo();
    this.cargarBancos();
  }

  // ----------------------------------------------------------------------------------------------
  // Permisos
  // ----------------------------------------------------------------------------------------------
  get puedeVer(): boolean { return this._auth.hasPermission('config.perfil_bancario.read'); }
  /** Los botones de escritura. El servidor lo vuelve a exigir con el permiso estricto. */
  get puedeCambiar(): boolean { return this._auth.hasPermission('config.cuentas_contrato.cambiar'); }

  // ----------------------------------------------------------------------------------------------
  // Carga
  // ----------------------------------------------------------------------------------------------

  /** Lee lo guardado y descarta el borrador (lo que se editó era sobre lo anterior). */
  async cargar(): Promise<void> {
    if (!this.puedeVer) return;
    this.cargando = true;
    this.error = null;
    try {
      const r = await this.servicio.getCuentasContrato();
      this.datos = r?.data || null;
      this.borrador = borradorVacio();
      this.avisoCorte = false;
      if (this.opcionAbierta && !this.datos?.opciones?.some((o: any) => String(o._id) === this.opcionAbierta)) this.opcionAbierta = null;
      this.recalcular();
    } catch (error: any) {
      console.error('❌ [CUENTAS-CONTRATO] Error cargando la vista:', error);
      this.datos = null;
      this.error = error?.error?.message || error?.error?.msg || 'No se pudieron consultar las cuentas por contrato';
    } finally {
      this.cargando = false;
    }
  }

  async cargarCatalogo(): Promise<void> {
    if (!this.puedeVer) return;
    try {
      const r = await this.servicio.getEtiquetas('', 200);
      this.catalogo = r?.data || [];
    } catch (error) {
      this.catalogo = [];
    }
  }

  /** Bancos del catálogo real (/api/invoice-credit/banks): nombre por código. Si falla, se usa el nombre que trae la cuenta. */
  async cargarBancos(): Promise<void> {
    if (!this.puedeVer) return;
    try {
      const r: any = await this._user.getBanks({});
      const mapa: { [codigo: string]: string } = {};
      for (const b of r?.res || []) if (b?.bankCode) mapa[b.bankCode] = b.name;
      this.bancos = mapa;
      this.listaBancos = (r?.res || []).filter((b: any) => b?.bankCode).map((b: any) => ({ bankCode: b.bankCode, name: b.name }))
        .sort((a: any, b: any) => String(a.name).localeCompare(String(b.name)));
      this.recalcular();
    } catch (error) {
      this.bancos = {};
      this.listaBancos = [];
    }
  }

  /** Todo lo que se deriva de lo guardado y el borrador. Se llama después de cada cambio. */
  recalcular(): void {
    if (!this.datos) {
      this.efectivo = null; this.bloqueos = []; this.avisos = []; this.frenos = []; this.cambios = []; this.totalCambios = 0; this.opciones = []; this.resumen = {};
      return;
    }
    this.efectivo = aplicarBorrador(this.datos, this.borrador);
    this.bloqueos = bloqueosDelBorrador(this.datos, this.borrador);
    this.avisos = avisosDelBorrador(this.datos, this.borrador);
    this.frenos = frenosDelBorrador(this.datos, this.borrador);
    this.cambios = listaDeCambios(this.datos, this.borrador, this.bancos);
    this.totalCambios = contarCambios(this.datos, this.borrador) + (simulaOtroContrato(this.datos, this.borrador) ? 1 : 0);
    this.opciones = opcionesPorPrioridad(this.efectivo);
    this.telefono = vistaTelefono(this.datos, this.borrador, this.modoTelefono, this.bancos);
    this.conteoEtiquetas = conteoDeEtiquetas(this.efectivo);

    const resumen: { [id: string]: any } = {};
    for (const o of this.opciones) {
      const id = String(o._id);
      const guardada = this.datos.opciones.find((x: any) => String(x._id) === id);
      const elegibles = cuentasDeOpcion(o);
      const visibles = elegibles.filter((c) => c.status !== false && c.showClient === true);
      resumen[id] = {
        mt: (o.cuentas?.mt || []).length,
        ct: (o.cuentas?.ct || []).length,
        visibles: visibles.length,
        bancosVisibles: Array.from(new Set(visibles.map((c) => this.nombreBanco(c)))).filter(Boolean),
        elige: elegibles.length > 0,
        cobros: (this.efectivo.cobros || []).filter((co: any) => String(co.opcion) === id),
        bloqueos: this.bloqueos.filter((b) => b.opcion === id || (b.cuenta && cuentasDeOpcion(o).some((c) => String(c._id) === b.cuenta))),
        conCambios: opcionTieneCambios(this.datos, this.borrador, guardada),
        pares: paresDeOpcion(o)
      };
    }
    this.resumen = resumen;
    this.paresFiltrados = this.filtroEtiqueta
      ? this.opciones.map((o) => ({ opcion: o, pares: paresConEtiqueta(o, this.filtroEtiqueta as string) })).filter((x) => x.pares.length)
      : [];
  }

  // ----------------------------------------------------------------------------------------------
  // Datos para la plantilla
  // ----------------------------------------------------------------------------------------------
  get totalCuentas(): number { return this.datos ? todasLasCuentas(this.datos).length : 0; }
  get hayBorrador(): boolean { return hayCambios(this.borrador); }
  get simulaCorte(): boolean { return !!this.datos && simulaOtroContrato(this.datos, this.borrador); }
  get opcionAbiertaDatos(): any { return this.opciones.find((o) => String(o._id) === this.opcionAbierta) || null; }

  /** ¿Esta cuenta tiene cambios sin guardar? */
  cuentaConCambios(c: any): boolean { return !!c?.nueva || !!this.borrador.cuentas[String(c._id)]; }
  esMT(c: any): boolean { return String(c?.perfil || 'MT').toUpperCase() === 'MT'; }
  /** «Clonar como CT» solo en una MT que todavía no tiene cuenta CT (ni guardada ni en el borrador). */
  puedeClonar(c: any): boolean { return !!c && this.esMT(c) && !c.nueva && !!this.efectivo && !parDe(this.efectivo, c); }
  bloqueosDeCuenta(c: any): any[] { return this.bloqueos.filter((b) => b.cuenta === String(c._id)); }

  nombreBanco(c: any): string {
    return c?.banco?.nombre || (c?.banco?.bankCode && this.bancos[c.banco.bankCode]) || c?.banco?.bankCode || c?.metodo?.name || '';
  }
  codigoBanco(c: any): string { return c?.banco?.bankCode || ''; }
  titular(c: any): string { return `${nombreContrato(c?.perfil || 'MT')}${c?.identity ? ' · ' + c.identity : ''}${c?.taxId ? ' · ' + c.taxId : ''}`; }
  /** Moneda de la cuenta, si el núcleo la manda. */
  monedaDe(c: any): string { const m = c?.moneda ?? c?.currency; return (m && typeof m === 'object' ? m.code || m.name : m) || ''; }
  textoVerificacion(c: any): string {
    const t = String(c?.typeConfirm || '').toUpperCase();
    if (t === 'AUTO' || t === 'AUTOMATIC') return 'Automática';
    if (t === 'MANUAL') return 'Manual';
    return c?.typeConfirm || '';
  }
  textoSap(c: any): string {
    return [c?.subscriptor_simbolo, c?.subscriptor_cuenta, c?.subscriptor_mp].some(Boolean)
      ? `símbolo ${c.subscriptor_simbolo || '—'} · cuenta ${c.subscriptor_cuenta || '—'} · MP ${c.subscriptor_mp || '—'}` : '';
  }
  /** La relación entre las dos cuentas de un par, en palabras. */
  relacion(c: any): string {
    const par = this.efectivo ? parDe(this.efectivo, c) : null;
    if (c.perfil && String(c.perfil).toUpperCase() !== 'MT') return par ? 'Reemplaza a la de Mundo Total' : 'Cuenta nueva de CrediTotal, sin par en Mundo Total';
    return par ? '' : 'Sin cuenta de CrediTotal';
  }
  nombreDeCuenta(c: any): string { return nombreCuenta(c, this.bancos); }
  colorEtiqueta(nombre: string): string { return this.catalogo.find((e) => e.nombre === nombre)?.color || '#64748b'; }
  fecha(f: any): string { return f ? new Date(f).toLocaleString('es-VE', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''; }

  get motivoValido(): boolean { return this.motivo.trim().length >= MOTIVO_MINIMO; }
  get puedeGuardar(): boolean {
    return this.puedeCambiar && !this.guardando && this.totalCambios > 0 && !this.bloqueos.length && !this.simulaCorte;
  }
  /** Por qué «Guardar» está apagado, en palabras. */
  get razonSinGuardar(): string {
    if (!this.puedeCambiar) return 'Solo lectura: no tienes el permiso para cambiar las cuentas por contrato.';
    if (!this.totalCambios) return 'Sin cambios. Mueve un interruptor y mira cómo queda la app.';
    if (this.bloqueos.length) return this.bloqueos.length === 1 ? 'Resuelve el bloqueo de arriba para poder guardar.' : `Resuelve los ${this.bloqueos.length} bloqueos de arriba para poder guardar.`;
    if (this.simulaCorte) return `Este borrador supone que cobra ${nombreContrato(this.borrador.perfilSimulado)}, pero hoy cobra ${nombreContrato(this.datos?.perfilCobra)}. Se aplica con «Pasar a ${this.borrador.perfilSimulado}» en Contrato activo, no desde aquí.`;
    return '';
  }

  // ----------------------------------------------------------------------------------------------
  // Editar el borrador
  // ----------------------------------------------------------------------------------------------
  private editar(nuevo: Borrador): void {
    this.borrador = nuevo;
    this.resultado = null;
    this.recalcular();
  }

  alternarOpcion(o: any): void { if (this.puedeCambiar) this.editar(ponerOpcion(this.datos, this.borrador, String(o._id), { showClient: !o.showClient })); }
  mover(o: any, direccion: 'subir' | 'bajar'): void { if (this.puedeCambiar) this.editar(moverOpcion(this.datos, this.borrador, String(o._id), direccion)); }
  alternarMostrar(c: any): void { if (this.puedeCambiar) this.editar(ponerCuenta(this.datos, this.borrador, String(c._id), { showClient: !c.showClient })); }
  alternarEncendida(c: any): void { if (this.puedeCambiar) this.editar(ponerCuenta(this.datos, this.borrador, String(c._id), { status: c.status === false })); }
  alternarChatbot(c: any): void { if (this.puedeCambiar) this.editar(ponerCuenta(this.datos, this.borrador, String(c._id), { showChatbot: !c.showChatbot })); }
  agregarEtiqueta(c: any, nombre: string): void { if (this.puedeCambiar) this.editar(agregarEtiqueta(this.datos, this.borrador, String(c._id), nombre)); }
  quitarEtiqueta(c: any, nombre: string): void { if (this.puedeCambiar) this.editar(quitarEtiqueta(this.datos, this.borrador, String(c._id), nombre)); }

  /** Una etiqueta recién creada entra al catálogo local para que tenga su color en todas las cuentas. */
  etiquetaCreada(etiqueta: any): void {
    if (etiqueta && !this.catalogo.some((e) => e.nombre === etiqueta.nombre)) this.catalogo = [...this.catalogo, etiqueta];
  }

  // ----------------------------------------------------------------------------------------------
  // Clonar como CT y editar una CT (ACUERDOS H)
  // ----------------------------------------------------------------------------------------------

  /** Abre el formulario con los datos de la MT copiados tal cual. */
  abrirClonar(mt: any): void {
    if (!this.puedeCambiar || !this.puedeClonar(mt)) return;
    const datos = datosDeCuenta(mt);
    const rif = this.datos?.perfilCT?.rif;
    if (rif) datos.taxId = rif; // el RIF de CrediTotal, no el de Mundo Total
    this.formulario = { modo: 'clonar', base: mt, origen: mt, tmpId: null, datos, avisoRif: !rif };
  }

  /** Abre el formulario con los datos de la CT (con lo ya editado en el borrador). Una CT nueva se edita en el borrador. */
  abrirEditar(ct: any): void {
    if (!this.puedeCambiar || !ct || this.esMT(ct)) return;
    const mt = ct.nueva ? buscarCuenta(this.datos, this.borrador.nuevas.find((n) => n.tmp_id === ct._id)?.clon_de || '') : null;
    this.formulario = { modo: 'editar', base: ct, origen: mt || ct, tmpId: ct.nueva ? String(ct._id) : null, datos: datosDeCuenta(ct) };
  }

  cerrarFormulario(): void { this.formulario = null; }

  /** Al elegir el banco del catálogo se guardan el _id y el código juntos. */
  elegirBanco(codigo: string): void {
    if (!this.formulario) return;
    this.formulario.datos.bankCode = codigo || null;
  }

  /** Lo que difiere de la cuenta de origen (la MT al clonar; la CT guardada al editar). Sin diferencias no hay nada que agregar. */
  get diferenciasFormulario(): DatosCuenta {
    const f = this.formulario;
    if (!f) return {};
    const origen = f.modo === 'clonar' || f.tmpId ? f.origen : buscarCuenta(this.datos, f.base._id) || f.base;
    return diferenciasConLaMT(origen, f.datos);
  }
  get formularioListo(): boolean { return !!this.formulario; }
  /** Un clon que no difiere en nada de la MT: se avisa, pero no se bloquea. */
  get clonIdentico(): boolean { return this.formulario?.modo === 'clonar' && Object.keys(this.diferenciasFormulario).length === 0; }
  get textoBotonFormulario(): string {
    return this.formulario?.modo === 'clonar' ? 'Agregar al borrador' : 'Guardar en el borrador';
  }

  aceptarFormulario(): void {
    const f = this.formulario;
    if (!f || !this.puedeCambiar || !this.formularioListo) return;
    const datos: DatosCuenta = {};
    for (const [k, v] of Object.entries(f.datos)) (datos as any)[k] = typeof v === 'string' ? v.trim() : v;
    if (f.modo === 'clonar') {
      this.contadorNuevas += 1;
      this.editar(clonarComoCT(this.datos, this.borrador, String(f.base._id), `nueva-${Date.now().toString(36)}-${this.contadorNuevas}`, datos));
      this.resaltar(`cuenta-${this.borrador.nuevas[this.borrador.nuevas.length - 1]?.tmp_id}`);
    } else if (f.tmpId) {
      this.editar(editarNueva(this.borrador, f.tmpId, datos));
    } else {
      this.editar(editarCuenta(this.datos, this.borrador, String(f.base._id), datos));
    }
    this.formulario = null;
  }

  /** Quita del borrador una cuenta CT nueva (todavía no se guardó nada). */
  quitarDelBorrador(c: any): void { if (this.puedeCambiar && c?.nueva) this.editar(quitarNueva(this.borrador, String(c._id))); }

  descartar(): void {
    this.borrador = borradorVacio();
    this.avisoCorte = false;
    this.resultado = null;
    this.recalcular();
  }

  /** «Preparar el corte del 1-nov»: llena el borrador. No guarda nada. */
  prepararElCorte(): void {
    if (!this.datos || !this.puedeCambiar) return;
    this.borrador = prepararCorte(this.datos);
    this.avisoCorte = true;
    this.modoTelefono = 'borrador';
    this.resultado = null;
    this.recalcular();
  }

  cambiarModoTelefono(modo: 'hoy' | 'borrador'): void {
    this.modoTelefono = modo;
    this.recalcular();
  }

  abrirOpcion(o: any): void { this.opcionAbierta = String(o._id); this.filtroEtiqueta = null; this.recalcular(); this.arriba('cuentas-contrato-niveles'); }
  volverAOpciones(): void { this.opcionAbierta = null; }

  filtrarPorEtiqueta(nombre: string | null): void {
    this.filtroEtiqueta = nombre;
    this.recalcular();
  }

  // ----------------------------------------------------------------------------------------------
  // Ir al bloqueo
  // ----------------------------------------------------------------------------------------------

  /** Lleva a la cuenta (abre su opción) o a la opción que causa el bloqueo, con scroll y resaltado. */
  irA(bloqueo: any): void {
    this.seccion = 'cuentas';
    this.filtroEtiqueta = null;
    let destino: string | null = null;
    if (bloqueo?.cuenta) {
      const cuenta = buscarCuenta(this.efectivo, bloqueo.cuenta);
      const opcionId = bloqueo.opcion || (cuenta?.payment_option ? String(cuenta.payment_option) : null);
      this.opcionAbierta = opcionId;
      destino = `cuenta-${bloqueo.cuenta}`;
    } else if (bloqueo?.opcion) {
      this.opcionAbierta = null;
      destino = `opcion-${bloqueo.opcion}`;
    }
    this.recalcular();
    if (destino) this.resaltar(destino);
  }

  private resaltar(idElemento: string): void {
    this.resaltado = idElemento;
    setTimeout(() => {
      const el = document.getElementById(idElemento);
      if (el) {
        const quieto = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
        el.scrollIntoView({ behavior: quieto ? 'auto' : 'smooth', block: 'center' });
      }
    }, 50);
    setTimeout(() => { if (this.resaltado === idElemento) this.resaltado = null; }, 2600);
  }

  private arriba(idElemento: string): void {
    setTimeout(() => document.getElementById(idElemento)?.scrollIntoView({ block: 'start' }), 0);
  }

  textoBloqueo(b: any): string { return b?.mensaje || ''; }
  /** «Ir a la cuenta», «Ir a la opción» o nada, si el bloqueo no señala nada (contrato desconocido). */
  textoIr(b: any): string { return b?.cuenta ? 'Ir a la cuenta' : b?.opcion ? 'Ir a la opción' : ''; }

  // ----------------------------------------------------------------------------------------------
  // Guardar
  // ----------------------------------------------------------------------------------------------
  abrirGuardar(): void {
    if (!this.puedeGuardar) return;
    this.motivo = '';
    this.bloqueosServidor = null;
    this.errorGuardar = null;
    this.dialogoGuardar = true;
  }

  cerrarGuardar(): void {
    if (this.guardando) return;
    this.dialogoGuardar = false;
  }

  async confirmarGuardar(): Promise<void> {
    if (!this.motivoValido || !this.puedeGuardar || !this.datos) return;
    this.guardando = true;
    this.bloqueosServidor = null;
    this.errorGuardar = null;
    const cantidad = this.totalCambios;
    try {
      await this.servicio.guardar(payloadDeCambios(this.datos, this.borrador), this.motivo.trim());
      this.dialogoGuardar = false;
      this.resultado = `Guardado. ${cantidad} ${cantidad === 1 ? 'cambio' : 'cambios'} en vivo para el cliente.`;
      alertifyjs.success('Cambios guardados');
      const mensaje = this.resultado;
      await this.cargar();
      this.resultado = mensaje;
      this.modoTelefono = 'hoy';
      this.recalcular();
      if (this.seccion === 'historial') this.cargarHistorial(1);
    } catch (error: any) {
      if (error?.status === 422 && error?.error?.bloqueos) {
        this.bloqueosServidor = error.error.bloqueos;
        this.errorGuardar = 'El servidor encontró bloqueos que aquí no se veían (alguien pudo cambiar algo desde otra pantalla). No se guardó nada.';
      } else if (error?.status === 409) {
        this.errorGuardar = `${error?.error?.message || 'No se pudo guardar.'} No se guardó nada.`;
      } else {
        // 400/422 del núcleo (datos inválidos, código repetido, MT sin par...): el mensaje se muestra tal cual
        this.errorGuardar = error?.error?.message || error?.error?.msg || error?.error?.errors?.[0]?.msg || 'No se pudo guardar. No se guardó nada; inténtalo de nuevo.';
      }
    } finally {
      this.guardando = false;
    }
  }

  // ----------------------------------------------------------------------------------------------
  // Historial
  // ----------------------------------------------------------------------------------------------
  abrirSeccion(seccion: 'cuentas' | 'historial'): void {
    this.seccion = seccion;
    if (seccion === 'historial' && !this.historial) this.cargarHistorial(1);
  }

  async cargarHistorial(pagina: number): Promise<void> {
    this.cargandoHistorial = true;
    this.errorHistorial = null;
    try {
      const r = await this.servicio.getCambios(pagina, this.limiteHistorial);
      this.historial = r?.data || null;
      this.paginaHistorial = this.historial?.pagina || pagina;
    } catch (error: any) {
      this.errorHistorial = error?.error?.message || 'No se pudo leer el historial de cambios';
    } finally {
      this.cargandoHistorial = false;
    }
  }

  get totalPaginasHistorial(): number {
    return Math.max(1, Math.ceil((this.historial?.total || 0) / (this.historial?.limite || this.limiteHistorial)));
  }

  /** ¿Ese cambio del historial creó alguna cuenta? Al volver no se borra: se apaga y se oculta. */
  creoCuentas(cambio: any): boolean { return hayCuentaCreada(cambio); }

  pedirVolver(cambio: any): void {
    if (!this.puedeCambiar || cambio?.revertido) return;
    this.cambioAVolver = cambio;
    this.motivoVolver = '';
    this.explicacionVolver = null;
  }

  cancelarVolver(): void {
    if (this.volviendo) return;
    this.cambioAVolver = null;
    this.explicacionVolver = null;
  }

  async confirmarVolver(): Promise<void> {
    if (!this.cambioAVolver || this.volviendo) return;
    this.volviendo = true;
    this.explicacionVolver = null;
    try {
      await this.servicio.volver(String(this.cambioAVolver._id), this.motivoVolver.trim() || undefined);
      this.cambioAVolver = null;
      alertifyjs.success('Se volvió al estado anterior');
      await this.cargar();
      this.resultado = 'Se restauró el estado anterior.';
      this.modoTelefono = 'hoy';
      this.recalcular();
      await this.cargarHistorial(this.paginaHistorial);
    } catch (error: any) {
      this.explicacionVolver = this.explicarVolver(error);
    } finally {
      this.volviendo = false;
    }
  }

  /** Los 409 y demás errores de «Volver a este estado», en palabras para alguien que no es técnico. */
  explicarVolver(error: any): { titulo: string; lineas: string[] } {
    const e = error?.error || {};
    if (error?.status === 409 && Array.isArray(e.choques) && e.choques.length) {
      return {
        titulo: 'No se puede volver todavía: después hubo otros cambios sobre las mismas cuentas',
        lineas: [
          ...e.choques.map((c: any) => `El ${this.fecha(c.fecha)}, ${c.usuario?.username || 'alguien'} hizo otro cambio${c.motivo ? ` («${c.motivo}»)` : ''} sobre ${(c.objetos || []).map((o: any) => o.code).join(', ')}.`),
          'Para volver a este estado, primero hay que volver ese cambio más reciente (el de arriba en el historial).'
        ]
      };
    }
    if (error?.status === 409 && Array.isArray(e.modificados) && e.modificados.length) {
      return {
        titulo: 'No se puede volver: algo se cambió por otro lado',
        lineas: [
          `Estas ya no están como las dejó este cambio: ${e.modificados.map((o: any) => o.code).join(', ')}.`,
          'Alguien las modificó desde otra pantalla (por ejemplo, Administrar cuentas) o con el corte del switch. Volver ahora pisaría ese cambio, así que no se hizo nada.',
          'Revisa cómo están hoy y, si hace falta, corrígelas a mano.'
        ]
      };
    }
    if (error?.status === 409) return { titulo: 'No se puede volver', lineas: [e.message || 'Ese cambio ya se había vuelto antes.'] };
    if (error?.status === 404) return { titulo: 'Ese cambio ya no existe', lineas: ['Actualiza el historial y vuelve a intentarlo.'] };
    return { titulo: 'No se pudo volver', lineas: [e.message || 'Inténtalo de nuevo. No se cambió nada.'] };
  }

  trackPorId = (_: number, x: any) => x?._id;
}
